<template>
  <div class="relative">
    <button 
      @click.prevent="toggle" 
      class="!bg-transparent justify-center"
      :class="{'bg-gray-100': menu?.overlayVisible }"
      aria-haspopup="true"
      :aria-expanded="menu?.overlayVisible?.toString()"
      :aria-controls="'overlay_menu_' + props.id"
    >
      <slot name="button-content">
        <div class="hover-rectangle">
          <i class="fa fas fa-ellipsis-v fa-lg text-gray-600"></i>
          <span class="sr-only">More Options</span>
        </div>
      </slot>
    </button>

    <Menu 
      ref="menu"
      :id="'overlay_menu_' + props.id"
      class="dropdown-menu"
      :model="menuItems"
      :popup="true"
      data-dropdown-menu
      data-disable-hover="true"
      data-click-open="true"
      data-alignment="right"
      @show="handleMenuShow"
    >
      <template #item="{ item }">
        <a 
          :href="item.url" 
          v-bind="item.dataAttrs"
          class="p-menuitem-link"
        >
          <span class="p-menuitem-text">{{ item.label }}</span>
        </a>
      </template>
    </Menu>
  </div>
</template>

<script setup>
import { ref, computed, nextTick } from "vue";
import Menu from "primevue/menu";

const menu = ref();

const props = defineProps({
  model: { type: Array, required: true },
  id: { type: String, required: true },
});

const decodeHtml = (html) => {
  if (!html) return html;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return doc.documentElement.textContent;
};

const menuItems = computed(() =>
  props.model.map((item) => {
    const dataAttrs = {};

    // Handle modal attributes
    if (item.modal) {
      Object.assign(dataAttrs, {
        "data-open-in": "modal-box",
        "data-action": "click->modal#open"
      });
    }

    // Handle Rails UJS attributes
    if (item.method && item.method !== 'get') {
      dataAttrs["data-method"] = item.method;
    }

    if (item.turbo_method && item.turbo_method !== 'get') {
      dataAttrs["data-turbo-method"] = item.turbo_method;
    }

    if (item.turbo_frame) {
      dataAttrs["data-turbo-frame"] = item.turbo_frame;
    }

    if (item.turbo_confirm) {
      dataAttrs["data-turbo-confirm"] = item.turbo_confirm;
    }

    if (item.turbo_stream) {
      dataAttrs["data-turbo-stream"] = "true";
    }

    if (item.remote) {
      dataAttrs["data-remote"] = "true";
    }

    if (item.turbo) {
      dataAttrs["data-turbo"] = "true";
    }

    if (item.aria_label) {
      dataAttrs["aria-label"] = item.aria_label;
    }

    if (item.confirm) {
      dataAttrs["data-confirm"] = item.confirm;
    }

    return {
      label: item.label,
      url: decodeHtml(item.url),
      dataAttrs: dataAttrs
    };
  })
);

const toggle = (event) => {
  menu.value?.toggle(event);
};

const handleMenuShow = async () => {
  // Wait for Vue to finish rendering the menu
  await nextTick();

  // Now the menu should be fully in the DOM
  const menuElement = document.querySelector('[data-pc-section="list"]');
  if (!menuElement) return;

  const menuRect = menuElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth;

  // Auto-position the menu
  if (menuRect.right > viewportWidth) {
    menuElement.classList.add("right-0");
    menuElement.classList.remove("left-0");
  } else {
    menuElement.classList.add("left-0");
    menuElement.classList.remove("right-0");
  }
};
</script>
